/* ============================================================================
   LA VISTA DE GESTIÓN — dónde entran los perfiles que no vuelan

   POR QUÉ ES OTRA APLICACIÓN Y NO LA MISMA CON PESTAÑAS DE MÁS

   La vista de operación está hecha para el piloto que va a despegar: peso y
   balance, combustible, cartas, cronómetro. **Todo eso es ruido para quien
   administra**, y una pantalla que muestra las dos cosas termina sirviendo mal
   a las dos: el que opera tiene que atravesar menús de administración, y el que
   administra tiene que aprender a ignorar instrumentos que nunca va a usar.

   Así que son dos vistas, y el perfil decide cuál —regla que vive en
   `src/domain/entrada.js`, con sus pruebas, y no en este archivo—.

   ═══ EL MENÚ NO SE ESCRIBE ACÁ ═══

   Las secciones se calculan con `seccionesDe(perfil)`, que filtra por la matriz
   de permisos. Si mañana un perfil gana un permiso, el menú se entera solo; y
   al revés, no puede ofrecer una puerta que el servidor va a cerrar con un 403
   delante del usuario.

   ═══ LAS SECCIONES QUE TODAVÍA NO TIENEN PANTALLA SE DICEN ═══

   Mantenimiento y auditoría viven dentro de la vista de operación y todavía no
   tienen pantalla propia. No se ofrece un botón que no lleva a ninguna parte:
   se dice dónde están. Un menú que esconde lo que falta hace creer que el
   sistema no lo hace.
   ========================================================================== */
import { useEffect, useState } from "react";

import PanelAdministracion from "../administracion/PanelAdministracion.jsx";
import PanelEstadisticas from "../estadisticas/PanelEstadisticas.jsx";
import Tablero from "./Tablero.jsx";
import { SECCIONES, seccionInicial, seccionesDe } from "../../domain/entrada.js";
import { identidadInicial, nombreDePerfil } from "../../domain/identidad.js";
import {
  actualizarEmpresa,
  actualizarUsuario,
  crearEmpresa,
  crearUsuario,
  listarEmpresas,
  listarUsuarios,
} from "../../services/administracion/api.js";
import PanelAsignaciones from "./PanelAsignaciones.jsx";
import PanelFacturacion from "./PanelFacturacion.jsx";
import { usePersistedState } from "../../services/db/persistencia.js";
import "./gestion.css";

export default function VistaDeGestion({
  usuario = null,
  alSalir,
  sello = null,
  alAbrirVuelo = null,
}) {
  const perfil = usuario?.perfil || null;

  /* ═══ LOS MISMOS ALMACENES QUE LA OPERACIÓN ═══

     La vista de gestión no tiene datos propios: lee los mismos que la vista de
     vuelo —la bitácora, la flota, los pilotos, la identidad y los clientes—.
     Duplicarlos sería tener dos libros de vuelo, y el primero que se
desincronice hace que un informe y una pantalla digan cosas distintas.

     No hay pelea entre las dos vistas porque **nunca están montadas a la vez**:
     el perfil decide cuál. Y por eso mismo los almacenes viven acá y no en el
cascarón: si vivieran en los dos lados, los dos escribirían su propio valor
inicial de empresa y el que escribiera último ganaría. */
  const [identidad, setIdentidad] = usePersistedState("identidad", identidadInicial());
  const [clientes, setClientes] = usePersistedState("clientes", [], undefined, "clientes");
  const [bitacora] = usePersistedState("bitacora", []);
  const [aeronaves] = usePersistedState("flota", []);
  const [pilotos] = usePersistedState("pilotos", []);
  /* Las asignaciones equipo → empresa viven en el dispositivo: el servidor
     todavía no tiene una entidad para ellas. Se guardan acá, junto a la flota,
     para que el historial no se pierda al cerrar la pestaña. */
  const [asignaciones, setAsignaciones] = usePersistedState("asignaciones", []);
  /* Las tarifas son un dato comercial del operador: se declaran acá y se
     guardan con su fuente. Sin tarifa, la factura no inventa un importe. */
  const [tarifas, setTarifas] = usePersistedState("tarifas", []);

  /* ═══ LA ADMINISTRACIÓN SE LEE DE LA BASE, NO DEL DISPOSITIVO ═══

     La identidad local era una maqueta: creaba usuarios en IndexedDB que el
     inicio de sesión —que consulta `usuarios` en MySQL— no podía encontrar.
     Con sesión abierta, esta pantalla lee y escribe por la API.

     `null` significa «todavía no se sabe». Si la lectura falla porque el
     servidor no tiene base, se cae a la identidad local y la propia pantalla
     avisa que esos usuarios no pueden entrar: la aplicación sigue funcionando,
     que es la regla de todo el proyecto. */
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    if (!usuario) return undefined;
    let vivo = true;

    Promise.all([listarEmpresas(), listarUsuarios()]).then(([empresas, usuarios]) => {
      if (!vivo) return;
      if (empresas.ok && usuarios.ok) {
        const propia = empresas.empresas.find((e) => e.id === String(usuario.empresaId))
          || empresas.empresas[0]
          || null;
        setAdmin({
          empresas: empresas.empresas,
          usuarios: usuarios.usuarios,
          empresaActiva: propia?.id || null,
        });
      } else {
        setAdmin(null);
      }
    });

    return () => { vivo = false; };
  }, [usuario?.id, usuario?.empresaId]);

  const apiAdministracion = admin !== null;

  const disponibles = seccionesDe(perfil);
  const [seccion, setSeccion] = useState(() => seccionInicial(perfil));

  const empresas = identidad?.empresas || [];
  const faltantes = seccionesDe(perfil, { soloConPanel: false }).filter((s) => !s.panel);
  const activa = disponibles.find((s) => s.clave === seccion) || disponibles[0] || null;

  const comun = { bitacora, aeronaves, pilotos, empresas, clientes };

  return (
    <div className="ges">
      <header className="ges__barra">
        <div className="ges__marca">
          <img src="/iconos/helicalc-icono.svg" alt="" width="26" height="26" />
          <span className="ges__marca-texto">HeliCalc</span>
          <span className="ges__marca-sep">·</span>
          <span className="ges__marca-vista">Gestión</span>
        </div>

        <div className="ges__quien">
          {usuario && (
            <span className="ges__usuario">
              <strong>{usuario.nombre}</strong>
              <small>
                {nombreDePerfil(perfil)}
                {usuario.empresa ? ` · ${usuario.empresa}` : ""}
              </small>
            </span>
          )}
          {/* ═══ LA PUERTA DE VUELTA, PARA EL QUE ADEMÁS VUELA ═══

              Un administrador de empresa no solo administra: vuela. Su perfil lo
              deja en gestión, así que sin este botón queda encerrado en el
              tablero, sin forma de abrir un plan de vuelo ni de registrar uno.

              No se dibuja para el auditor ni para mantenimiento —que no tienen
              `vuelos.escribir`— y por eso es una prop y no una condición
              escrita acá: quien decide es la matriz de permisos, en
              `vistasDe()` del dominio. */}
          {alAbrirVuelo && (
            <button type="button" className="ges__a-vuelo" onClick={alAbrirVuelo}>
              Abrir la vista de operación
            </button>
          )}
          <button type="button" className="ges__salir" onClick={alSalir}>
            Cerrar sesión
          </button>
        </div>
      </header>

      <nav className="ges__nav" aria-label="Secciones de gestión">
        {disponibles.map((s) => (
          <button
            key={s.clave}
            type="button"
            className={`ges__nav-btn${s.clave === activa?.clave ? " es-actual" : ""}`}
            aria-pressed={s.clave === activa?.clave}
            onClick={() => setSeccion(s.clave)}
          >
            {s.nombre}
          </button>
        ))}
      </nav>

      <main className="ges__cuerpo">
        {!activa ? (
          /* Un perfil sin ninguna sección disponible no ve una pantalla en
             blanco: sabe por qué. Es el caso de un rol al que le faltan los
             permisos, o de uno que no existe. */
          <p className="ges__bloqueo" role="alert">
            {nombreDePerfil(perfil)} no tiene ninguna sección de gestión disponible. Si crees
            que es un error, pídele a quien administra la plataforma que revise tu perfil.
          </p>
        ) : (
          <>
            <p className="ges__descripcion">{activa.descripcion}</p>

            {activa.clave === "tablero" && <Tablero {...comun} />}

            {activa.clave === "estadisticas" && <PanelEstadisticas {...comun} />}

            {activa.clave === "equipos" && (
              <PanelAsignaciones
                aeronaves={aeronaves}
                empresas={empresas}
                asignaciones={asignaciones}
                alCambiarAsignaciones={setAsignaciones}
                perfil={perfil}
                usuario={usuario}
              />
            )}

            {activa.clave === "facturacion" && (
              <PanelFacturacion
                bitacora={bitacora}
                clientes={clientes}
                tarifas={tarifas}
                alCambiarTarifas={setTarifas}
                perfil={perfil}
                usuario={usuario}
              />
            )}

            {activa.clave === "empresa" && (
              <PanelAdministracion
                estado={apiAdministracion ? admin : identidad}
                alCambiar={apiAdministracion ? setAdmin : setIdentidad}
                clientes={clientes}
                alCambiarClientes={setClientes}
                /* El perfil ya no se simula: es el de la sesión. */
                perfilQueMira={perfil}
                alCambiarPerfil={null}
                /* Con la base disponible, estas son las llamadas reales. Sin
                   ella quedan en `null` y la pantalla lo dice. */
                alCrearUsuario={apiAdministracion ? crearUsuario : null}
                alActualizarUsuario={apiAdministracion ? actualizarUsuario : null}
                alCrearEmpresa={apiAdministracion ? crearEmpresa : null}
                alActualizarEmpresa={apiAdministracion ? actualizarEmpresa : null}
              />
            )}

            {faltantes.length > 0 && activa.clave === disponibles[0].clave && (
              <aside className="ges__sin-panel">
                <h4 className="ges__sin-panel-titulo">Todavía no tienen pantalla propia</h4>
                <ul className="ges__sin-panel-lista">
                  {faltantes.map((s) => (
                    <li key={s.clave}>
                      <strong>{s.nombre}</strong> — {s.descripcion} Vive en la vista de
                      operación.
                    </li>
                  ))}
                </ul>
                <p className="ges__sin-panel-nota">
                  Se declaran acá para que la falta se vea. Un menú que las escondiera haría
                  creer que el sistema no las hace.
                </p>
              </aside>
            )}
          </>
        )}
      </main>

      <footer className="ges__pie">
        <span>
          Herramienta de apoyo a la decisión. No reemplaza el RFM ni a la autoridad
          aeronáutica.
        </span>
        {sello && <span className="ges__sello">{sello}</span>}
        <span className="ges__pie-nota">
          De {SECCIONES.length} secciones declaradas, {disponibles.length} son de este perfil.
        </span>
      </footer>
    </div>
  );
}
